<template>
  <modal
    name="assign-vehicle-modal"
    :height="isMobile ? '100%' : 'auto'"
    :width="isMobile ? '100%' : '550px'"
    @closed="$emit('refresh')"
    class="rounded-lg overflow-hidden"
  >
    <!-- Header -->
    <div class="bg-gradient-to-r from-red-600 to-red-700 py-4 px-5 flex justify-between items-center dark:text-white">
      <h4 class="text-lg font-semibold text-white flex items-center">
        <i class="fas fa-truck-moving mr-2"></i>
        ARAÇ GÖREV ATAMA
      </h4>
      <button class="text-white hover:bg-red-800 p-2 rounded-full transition-colors duration-200" @click="$modal.hide('assign-vehicle-modal')">
        <i class="fas fa-times"></i>
      </button>
    </div>

    <!-- Content -->
    <div class="p-5 bg-white dark:bg-gray-800 text-black dark:text-gray-200 max-h-[calc(100vh-150px)] overflow-y-auto">
      <!-- Araç Bilgileri -->
      <div class="mb-6">
        <h5 class="font-bold mb-3 text-gray-700 dark:text-gray-300 flex items-center">
          <i class="fas fa-info-circle mr-2 text-red-600"></i>
          Araç Bilgileri
        </h5>
        <div class="flex flex-col space-y-2 p-4 bg-gray-100 dark:bg-gray-700 rounded-lg border-l-4 border-red-600">
          <div class="flex justify-between items-center">
            <span class="font-semibold text-gray-600 dark:text-gray-300">Plaka:</span>
            <span class="bg-gray-200 dark:bg-gray-600 px-3 py-1 rounded-md">{{ vehicle?.plaque }}</span>
          </div>
          <div class="flex justify-between items-center">
            <span class="font-semibold text-gray-600 dark:text-gray-300">Sürücü:</span>
            <span>{{ vehicle?.NAME }}</span>
          </div>
          <div class="flex justify-between items-center">
            <span class="font-semibold text-gray-600 dark:text-gray-300">Dorse:</span>
            <span>{{ vehicle?.dorse || "Belirtilmemiş" }}</span>
          </div>
        </div>
      </div>
      
      <!-- Sipariş Seçimi -->
      <div class="mb-6">
        <h5 class="font-bold mb-3 text-gray-700 dark:text-gray-300 flex items-center">
          <i class="fas fa-clipboard-list mr-2 text-red-600"></i>
          Atanacak Sipariş
        </h5>
        <div class="relative">
          <select 
            v-model="selectedOrderId" 
            class="w-full p-3 border dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-red-600 focus:border-red-600 transition-all duration-200 appearance-none"
          >
            <option value="" disabled>Sipariş seçiniz</option>
            <option 
              v-for="order in getOrderAll" 
              :key="order.id" 
              :value="order.id"
            >
              {{ order.orderNo }} - {{ order.orderName }}
            </option>
          </select>
          <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700 dark:text-gray-300">
            <i class="fas fa-chevron-down"></i>
          </div>
        </div>

        <!-- Sipariş Detayları -->
        <transition name="fade">
          <div v-if="selectedOrder" class="mt-4 p-4 bg-gray-100 dark:bg-gray-700 rounded-lg border-l-4 border-blue-600">
            <h6 class="font-semibold mb-2 text-blue-600 dark:text-blue-400">Sipariş Detayı</h6>
            <div class="grid grid-cols-2 gap-3">
              <div class="col-span-2 sm:col-span-1">
                <span class="text-xs font-medium text-gray-500 dark:text-gray-400">Ürün:</span>
                <p>{{ selectedOrder.productType || "Belirtilmemiş" }}</p>
              </div>
              <div class="col-span-2 sm:col-span-1">
                <span class="text-xs font-medium text-gray-500 dark:text-gray-400">Dolum:</span>
                <p>{{ selectedOrder.receivedName || "Belirtilmemiş" }}</p>
              </div>
              <div class="col-span-2 sm:col-span-1">
                <span class="text-xs font-medium text-gray-500 dark:text-gray-400">Teslim:</span>
                <p>{{ selectedOrder.deliveredName || "Belirtilmemiş" }}</p>
              </div>
              <div class="col-span-2 sm:col-span-1">
                <span class="text-xs font-medium text-gray-500 dark:text-gray-400">Sipariş No:</span>
                <p>{{ selectedOrder.orderNo || "Belirtilmemiş" }}</p>
              </div>
            </div>
          </div>
        </transition>
      </div>
      
      <!-- Atama Butonu -->
      <div class="mt-6">
        <asyncBtn
          icon="fas fa-share"
          text="Göreve Ata"
          :loadState="loading"
          class="w-full hover:shadow-lg transition-all duration-300"
          :disabled="!selectedOrderId"
          :containerClass="selectedOrderId ? 'bg-red-600 hover:bg-red-700' : 'bg-gray-400 cursor-not-allowed'"
          @click="assignVehicle"
        />
      </div>
    </div>
  </modal>
</template>

<script>
import asyncBtn from "@/components/general/asyncBtn.vue";
import axios from "axios";
import { bulkShipPlanning } from "@/networking/urlmanager";

export default {
  props: {
    rowId: {
      type: [String, Number],
      required: true
    },
    vehicle: {
      type: Object,
      default: () => ({})
    },
    orders: {
      type: Array,
      default: () => []
    }
  },
  components: {
    asyncBtn
  },
  data() {
    return {
      selectedOrderId: "",
      loading: false
    };
  },
  computed: {
    getOrderAll(){
      return this.orders.filter(r => {
        // Tonaj 0 veya negatif olan siparişleri filtrele
        if (r.tonnage <= 0) return false;

        // Tamamlanmış siparişleri filtrele (isCopmletedOrder = 1)
        if (r.isCopmletedOrder === 1) return false;

        // Kalan tonajı olmayan siparişleri filtrele (tonnage - receivedTonnage = 0)
        const remainingTonnage = Number(r.tonnage) - Number(r.receivedTonnage || 0);
        if (remainingTonnage <= 0) return false;

        return true;
      });
    },  
    selectedOrder() {
      return this.orders.find(order => order.id === this.selectedOrderId);
    },
    isMobile() {
      return window.innerWidth < 768;
    }
  },
  methods: {
    assignVehicle() {
      if (!this.selectedOrderId || !this.vehicle?.id) {
        this.swalBox(
          "warning",
          "Uyarı!",
          "Lütfen sipariş seçiniz",
          false,
          "Tamam"
        );
        return;
      }
      
      this.loading = true;
      axios
        .post(
          bulkShipPlanning.assignVehicle,
          {
            vehicleId: this.vehicle.id,
            orderId: this.selectedOrderId,
            shipId: this.rowId
          },
          {
            headers: {
              Authorization: "Bareer " + this.$store.state.userData.token
            }
          }
        )
        .then(() => {
          this.swalBox(
            "success",
            this.$t("general.successTitle"),
            "Araç başarıyla siparişe atandı.",
            false,
            this.$t("general.OkayTitle")
          );
          this.$modal.hide("assign-vehicle-modal");
          this.$emit("refresh");
        })
        .catch(err => {
          this.errorBox(err.response);
        })
        .finally(() => {
          this.loading = false;
        });
    }
  },
  watch: {
    vehicle() {
      // Araç değiştiğinde seçili siparişi sıfırla
      this.selectedOrderId = "";
    }
  }
};
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
